import { useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet";
import { apiRequest } from "@/lib/api";

// UI Components
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Icons
import {
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Eye,
  Clock,
  Check,
  Star,
  Flag,
  Bookmark,
  CornerUpRight,
} from "lucide-react";
import { format } from "date-fns";

type CommunityQuestion = {
  id: number;
  title: string;
  content: string;
  authorId: number;
  authorName?: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  upvotes: number;
  downvotes: number;
  views: number;
  isModeratorPinned: boolean;
  tags: Array<{ id: number; name: string }>;
  answers: CommunityAnswer[];
};

type CommunityAnswer = {
  id: number;
  content: string;
  authorId: number;
  authorName?: string;
  questionId: number;
  createdAt: Date;
  updatedAt: Date;
  upvotes: number;
  downvotes: number;
  isAcceptedAnswer: boolean;
  isModeratorVerified: boolean;
};

export default function CommunityQuestionPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newAnswer, setNewAnswer] = useState("");
  const [isReporting, setIsReporting] = useState(false);

  // Fetch question details
  const {
    data: question,
    isLoading,
    error,
  } = useQuery({
    queryKey: [`community/questions/${id}`],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', `community/questions/${id}`);
        
        // if (!response.ok) {
        //   throw new Error(`Failed to fetch question: ${response.statusText}`);
        // }
        
        // return response.json();
        return response;
      } catch (error) {
        console.error("Error fetching question:", error);
        throw error;
      }
    },
    enabled: !!user && !!id,
  });

  // Submit answer mutation
  const submitAnswerMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await apiRequest(
        "POST",
        `community/questions/${id}/answers`,
        { content }
      );
      return response.json();
    },
    onSuccess: () => {
      // Reset form and refetch question data
      setNewAnswer("");
      queryClient.invalidateQueries({ queryKey: [`community/questions/${id}`] });
      toast({
        title: "Answer submitted",
        description: "Your answer has been posted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to submit answer",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  // Upvote question mutation
  const upvoteQuestionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `community/questions/${id}/upvote`
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`community/questions/${id}`] });
      toast({
        title: "Question upvoted",
        description: "Your vote has been recorded.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to upvote",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  // Upvote answer mutation
  const upvoteAnswerMutation = useMutation({
    mutationFn: async (answerId: number) => {
      const response = await apiRequest(
        "POST",
        `community/answers/${answerId}/upvote`
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`community/questions/${id}`] });
      toast({
        title: "Answer upvoted",
        description: "Your vote has been recorded.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to upvote",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  // Accept answer mutation
  const acceptAnswerMutation = useMutation({
    mutationFn: async (answerId: number) => {
      const response = await apiRequest(
        "POST",
        `community/answers/${answerId}/accept`
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`community/questions/${id}`] });
      toast({
        title: "Answer accepted",
        description: "This answer has been marked as accepted.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to accept answer",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  // Follow question mutation
  const followQuestionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `community/questions/${id}/follow`
      );
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Following question",
        description: "You'll be notified of updates to this question.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to follow question",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  const handleSubmitAnswer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAnswer.trim()) {
      toast({
        title: "Empty answer",
        description: "Please write your answer before submitting.",
        variant: "destructive",
      });
      return;
    }

    submitAnswerMutation.mutate(newAnswer);
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Link href="/manager/community" className="flex items-center mb-4 text-sm">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Community
        </Link>
        <h1 className="text-2xl font-bold mb-4">Question</h1>
        <p className="text-red-500">
          Error loading question. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isLoading
            ? "Loading Question... | StayMiddle"
            : `${question?.title} | StayMiddle`}
        </title>
      </Helmet>

      <div className="container mx-auto py-6">
        <Link href="/manager/community" className="flex items-center mb-4 text-sm">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Community
        </Link>

        {isLoading ? (
          <div className="space-y-4">
            <div className="h-8 w-3/4 bg-muted rounded animate-pulse"></div>
            <div className="h-4 w-1/3 bg-muted rounded animate-pulse"></div>
            <div className="space-y-2 mt-6">
              <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
              <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
              <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
              <div className="h-4 w-3/4 bg-muted rounded animate-pulse"></div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between">
                  <div>
                    <CardTitle className="text-2xl font-bold">
                      {question?.title}
                    </CardTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {question?.tags.map((tag) => (
                        <Badge key={tag.id} variant="outline">
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => followQuestionMutation.mutate()}
                    >
                      <Bookmark className="h-4 w-4 mr-2" />
                      Follow
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Flag className="h-4 w-4 mr-2" />
                          Report
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Report question</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to report this question? A
                            moderator will review it for inappropriate content or
                            violations of community guidelines.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              toast({
                                title: "Question reported",
                                description:
                                  "Thank you for helping keep our community safe.",
                              });
                            }}
                          >
                            Report
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-muted-foreground mt-2">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      Asked{" "}
                      {format(new Date(question?.createdAt), "MMM d, yyyy")}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" />
                      {question?.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-3.5 w-3.5" />
                      {question?.answers?.length || 0} answers
                    </span>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="flex">
                  <div className="flex flex-col items-center mr-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => upvoteQuestionMutation.mutate()}
                    >
                      <ThumbsUp className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">
                      {question?.upvotes}
                    </span>
                    <Button variant="ghost" size="sm">
                      <ThumbsDown className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex-1">
                    <div className="prose prose-sm max-w-none">
                      <p>{question?.content}</p>
                    </div>

                    <div className="flex justify-end mt-6">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <div className="mr-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback>
                              {question?.authorName?.[0] || "U"}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <span>
                          Asked by{" "}
                          <span className="font-medium">
                            {question?.authorName || "User"}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-xl font-bold mt-8 mb-4">
              {question?.answers?.length || 0} Answers
            </h2>

            {question?.answers?.length > 0 ? (
              <div className="space-y-6">
                {/* Sort answers to show accepted one first */}
                {[...question.answers]
                  .sort((a, b) => (a.isAcceptedAnswer ? -1 : b.isAcceptedAnswer ? 1 : 0))
                  .map((answer) => (
                    <Card
                      key={answer.id}
                      className={
                        answer.isAcceptedAnswer
                          ? "border-green-500 border-2"
                          : ""
                      }
                    >
                      {answer.isAcceptedAnswer && (
                        <div className="bg-green-500 text-white text-xs font-medium px-3 py-1 flex items-center">
                          <Check className="h-3 w-3 mr-1" />
                          Accepted Answer
                        </div>
                      )}
                      <CardContent className="pt-6">
                        <div className="flex">
                          <div className="flex flex-col items-center mr-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                upvoteAnswerMutation.mutate(answer.id)
                              }
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </Button>
                            <span className="text-sm font-medium">
                              {answer.upvotes}
                            </span>
                            <Button variant="ghost" size="sm">
                              <ThumbsDown className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="flex-1">
                            <div className="prose prose-sm max-w-none">
                              <p>{answer.content}</p>
                            </div>

                            <div className="flex justify-between mt-6">
                              {/* Accept answer button (visible only to question author) */}
                              {!answer.isAcceptedAnswer &&
                                question.authorId === user?.id && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-green-600"
                                    onClick={() =>
                                      acceptAnswerMutation.mutate(answer.id)
                                    }
                                  >
                                    <Check className="h-4 w-4 mr-2" />
                                    Accept Answer
                                  </Button>
                                )}

                              <div className="flex items-center text-sm text-muted-foreground ml-auto">
                                <div className="mr-2">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback>
                                      {answer.authorName?.[0] || "U"}
                                    </AvatarFallback>
                                  </Avatar>
                                </div>
                                <span>
                                  Answered by{" "}
                                  <span className="font-medium">
                                    {answer.authorName || "User"}
                                  </span>{" "}
                                  on{" "}
                                  {format(
                                    new Date(answer.createdAt),
                                    "MMM d, yyyy"
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-6 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    No answers yet. Be the first to answer this question!
                  </p>
                </CardContent>
              </Card>
            )}

            <h2 className="text-xl font-bold mt-8 mb-4">Your Answer</h2>
            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmitAnswer} className="space-y-4">
                  <Textarea
                    value={newAnswer}
                    onChange={(e) => setNewAnswer(e.target.value)}
                    placeholder="Write your answer here..."
                    rows={6}
                    required
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitAnswerMutation.isPending}
                  >
                    {submitAnswerMutation.isPending
                      ? "Submitting..."
                      : "Post Your Answer"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}